import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import MakeItLiveCta from "../components/MakeItLiveCta.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/swiss-system-tournament-generator')({
    component: RouteComponent,
})

type SwissPlayer = {name: string; points: number; seed: number; opponents: string[]};
type SwissPairing = {p1: string; p1Pts: number; p2: string; p2Pts: number; bye: boolean};
type SwissRound = {title: string; pairings: SwissPairing[]};

/**
 * Swiss pairings: each round sorts by score (then seed), pairs the top remaining player with the
 * highest-scoring opponent they haven't faced, and gives any odd player a bye. A simple demo scoring
 * pass advances the higher-ranked player so later rounds show Swiss-style grouping.
 */
function buildSwiss(names: string[], rounds: number): SwissRound[] {
    const standings: SwissPlayer[] = names.map((name, i) => ({name, points: 0, seed: i + 1, opponents: []}));
    const result: SwissRound[] = [];

    for (let round = 1; round <= rounds; round++) {
        standings.sort((a, b) => (b.points !== a.points ? b.points - a.points : a.seed - b.seed));

        const available = [...standings];
        const paired: [SwissPlayer, SwissPlayer][] = [];

        while (available.length > 1) {
            const player = available.shift() as SwissPlayer;
            let opponentIndex = available.findIndex((c) => !player.opponents.includes(c.name));
            if (opponentIndex === -1) opponentIndex = 0;
            const opponent = available.splice(opponentIndex, 1)[0];
            player.opponents.push(opponent.name);
            opponent.opponents.push(player.name);
            paired.push([player, opponent]);
        }

        const byePlayer = available.length === 1 ? available[0] : null;
        if (byePlayer) byePlayer.points += 1; // a bye is worth a point

        const pairings: SwissPairing[] = paired.map(([a, b]) => ({p1: a.name, p1Pts: a.points, p2: b.name, p2Pts: b.points, bye: false}));
        if (byePlayer) pairings.push({p1: byePlayer.name, p1Pts: byePlayer.points, p2: "BYE", p2Pts: 0, bye: true});

        result.push({title: `Round ${round}`, pairings});

        // Demo-only: advance the higher-ranked player so future rounds group by record.
        paired.forEach(([a]) => {
            a.points += 1;
        });
    }

    return result;
}

const STEPS = [
    {title: "Start With Round 1", description: "Initial pairings are usually random, seeded or rating-based."},
    {title: "Group Similar Records", description: "Players with similar results are paired together in later rounds."},
    {title: "Rank By Performance", description: "Final standings are based on wins, points and tiebreakers."},
];

const VS_ROWS = [
    {feature: "Everyone plays everyone", sw: "No", rr: "Yes"},
    {feature: "Good for large events", sw: "Yes", rr: "No"},
    {feature: "Fair rankings", sw: "Good", rr: "Excellent"},
    {feature: "Match volume", sw: "Medium", rr: "High"},
    {feature: "Repeat matchups avoided", sw: "Usually", rr: "Not applicable"},
];

const BEST_FOR = ["Large tournaments", "Chess-style events", "Esports competitions", "Competitive club events", "Events with limited rounds"];
const LESS_IDEAL = ["Small social events", "Simple club nights", "Events where everyone must play everyone", "Traditional knockout championships", "Beginner-friendly formats needing simplicity"];

const FAQS = [
    {
        q: 'What is a Swiss system tournament?',
        a: <>A Swiss system tournament is a format where players compete for a fixed number of rounds and are paired against opponents with similar records, without everyone needing to play everyone.</>,
    },
    {
        q: 'How many rounds are needed in a Swiss tournament?',
        a: <>It depends on the number of players and how precise the final ranking needs to be. Many events use 4 to 7 rounds.</>,
    },
    {
        q: 'Can players face the same opponent twice?',
        a: <>In most Swiss systems, repeat matchups are avoided where possible. The generator skips opponents a player has already faced when it can.</>,
    },
    {
        q: 'Is Swiss system better than knockout?',
        a: <>Swiss gives players more matches and better rankings than a <Link to={"/single-elimination-bracket-generator/" as any} className="text-[#082F8A] underline font-semibold">knockout</Link>, but knockout is faster and simpler.</>,
    },
    {
        q: 'Can ScorePal manage Swiss system scores?',
        a: <>Yes. ScorePal manages fixtures, scores, standings, rankings and tournament communication for Swiss system events.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [rounds, setRounds] = useState("4");
    const [schedule, setSchedule] = useState<SwissRound[] | null>(null);
    const [summary, setSummary] = useState<{players: number; rounds: number; perRound: number; hasBye: boolean} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Ranking the field", "Pairing round by round", "Balancing byes"]);

    // Same handoff the results panel uses — hoisted so the top CTA and MakeItLiveCta can't drift.
    const buildPayload = () => {
        const teams = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        return teams.length >= 2 ? {source: "swiss-system-tournament-generator", format: "swiss" as const, sport: "padel", teams, rounds: parseInt(rounds, 10) || undefined} : null;
    };

    const generate = () => {
        const players = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (players.length < 2) {
            setError("Please enter at least two players or teams.");
            setSchedule(null);
            setSummary(null);
            return;
        }
        const roundCount = Math.min(12, Math.max(1, parseInt(rounds, 10) || 4));
        setError(null);
        setSchedule(null);
        setSummary(null);
        gen.run(() => {
            setSchedule(buildSwiss(players, roundCount));
            setSummary({
                players: players.length,
                rounds: roundCount,
                perRound: Math.ceil(players.length / 2),
                hasBye: players.length % 2 !== 0,
            });
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Swiss System Tournament Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a Swiss system tournament schedule instantly. Generate Swiss pairings for chess, esports, padel, tennis, football, basketball, baseball, cricket, rugby and competitive club events with ScorePal's free Swiss tournament generator." />
            <link rel="canonical" href={`${config.publicUrl}/swiss-system-tournament-generator/`} />
            <meta property="og:title" content="Swiss System Tournament Generator | ScorePal" />
            <meta property="og:description" content="Generate Swiss system tournament pairings for competitive events, clubs and leagues." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/swiss-system-tournament-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/swiss-system-tournament-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Swiss System Tournament Generator | ScorePal" />
            <meta name="twitter:description" content="Create Swiss system tournament schedules instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Swiss System Tournament Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate Swiss system tournament pairings for sports, clubs and competitive events.",
                "url": "https://scorepal.co/swiss-system-tournament-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a Swiss system tournament?", "acceptedAnswer": {"@type": "Answer", "text": "A Swiss system tournament is a format where participants play a fixed number of rounds against opponents with similar scores, without everyone needing to play everyone else."}},
                    {"@type": "Question", "name": "How are Swiss system pairings made?", "acceptedAnswer": {"@type": "Answer", "text": "Swiss pairings usually match participants with similar records while avoiding repeat matchups where possible."}},
                    {"@type": "Question", "name": "When should I use a Swiss system tournament?", "acceptedAnswer": {"@type": "Answer", "text": "Swiss system tournaments are useful for large events where a full round robin would take too long but fair rankings are still important."}},
                    {"@type": "Question", "name": "Can players face the same opponent twice?", "acceptedAnswer": {"@type": "Answer", "text": "In most Swiss systems, repeat matchups are avoided where possible."}},
                    {"@type": "Question", "name": "Can ScorePal manage Swiss system scores?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, scores, standings, rankings and tournament communication for Swiss system events."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Swiss System Tournament Generator", item: "https://scorepal.co/swiss-system-tournament-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Tournament Pairing Tool
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Swiss System Tournament Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create Swiss system tournament pairings for competitive events where players face
                                    opponents with similar records. Ideal for large tournaments where a full round robin
                                    would take too long.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Swiss Draw</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players or teams and choose how many rounds to play.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players or teams</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player or team per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4\nPlayer 5\nPlayer 6\nPlayer 7\nPlayer 8"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <label htmlFor="rounds" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">Number of rounds</label>
                                <input
                                    id="rounds"
                                    type="number"
                                    min={1}
                                    max={12}
                                    value={rounds}
                                    onChange={(e) => setRounds(e.target.value)}
                                    className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate Swiss Pairings"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.players}</strong> participants · <strong>{summary.rounds}</strong> rounds ·{" "}
                                        <strong>{summary.perRound}</strong> pairings per round
                                        {summary.hasBye ? " · includes one bye per round" : ""}
                                    </p>
                                )}

                                {schedule && <GeneratorTopCta buildPayload={buildPayload} />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {schedule && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                            Your Swiss System Pairings
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 mb-8">In a real Swiss event, adjust later rounds based on actual scores, standings and repeat-matchup rules.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {schedule.map((round) => (
                                <div key={round.title} className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-5">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold mb-3">{round.title}</div>
                                    <div className="space-y-2.5">
                                        {round.pairings.map((p, i) => (
                                            <div key={i} className="bg-white border border-[#e5e7eb] rounded-xl p-3 font-inter text-sm flex items-center gap-2 flex-wrap">
                                                <span className="inline-flex bg-[#EDEEF4] text-[#082F8A] font-semibold rounded-full px-2 py-0.5 text-xs">{p.p1Pts} pts</span>
                                                <span className="font-semibold text-[#1a1a2e]">{p.p1}</span>
                                                <span className="text-[#1a1a2e]/40">vs</span>
                                                {p.bye ? (
                                                    <span className="text-[#1a1a2e]/40 italic">BYE</span>
                                                ) : (
                                                    <>
                                                        <span className="inline-flex bg-[#EDEEF4] text-[#082F8A] font-semibold rounded-full px-2 py-0.5 text-xs">{p.p2Pts} pts</span>
                                                        <span className="font-semibold text-[#1a1a2e]">{p.p2}</span>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <MakeItLiveCta formatLabel="Swiss" buildPayload={buildPayload} />
                    </div>
                </section>
            )}

            {/* What is Swiss / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Swiss System Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A Swiss system tournament is a format where participants play a fixed number of rounds against
                        opponents with similar scores. It is commonly used when there are too many players for a full round
                        robin but the organizer still wants fair standings.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Swiss vs Round Robin */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Swiss System vs Round Robin
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Swiss is useful when a full <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link> would create too many matches.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Swiss System</div>
                                <div className="p-5 text-[#1a1a2e]">Round Robin</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.sw}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.rr}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use a Swiss System Format?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Swiss System Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate pairings, track scores, manage standings and publish live rankings from one tournament management platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Swiss System", "Round Robin", "Knockout", "Live Scores", "Player Rankings", "Club Events"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Swiss System FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
